import { LocationProvider, Router, Route, ErrorBoundary, useLocation } from 'preact-iso'

// Import components directly
import { SignIn, RootRedirect, NotFound } from './components'
import { WorkspaceShell } from './components/layout/WorkspaceShell'
import { Section } from '@/utils/routes'
import { CompanyBootstrap } from './components/CompanyBootstrap'
import { Dashboard } from './components/Dashboard'
import { CompanyProfilePage } from './components/companies/CompanyProfilePage'
import { Campaigns } from './components/Campaigns'
import { WorkspaceLeads } from './components/leads/WorkspaceLeads'
import { Audience } from './components/Audience'
import { WorkspaceEmailActivityPage } from './components/emails/WorkspaceEmailActivityPage'
import { ScheduleXCalendar } from './components/calendar/ScheduleXCalendar'
import { WorkspaceSettingsPage } from './components/workspace/WorkspaceSettingsPage'
import { KnowledgeBasePage } from './components/knowledgeBase/KnowledgeBasePage'
import { CampaignDetail } from './components/campaigns/CampaignDetail'
import { AudienceDetail } from './components/AudienceDetail'
import { WebDetail } from './components/webs/WebDetail'
import { BlastDetailPage } from './components/webs/email-blasts/BlastDetailPage'
import { ContactDetailPage } from './components/contacts/ContactDetailPage'
import type { ContactAnchorType } from '@/api/contactDetail'
import { ProtectedRoute } from './components/ProtectedRoute'
import { Settings } from './components/settings/Settings'
import { AuthProvider } from './auth/AuthContext'
import { useAuth } from './auth/useAuth'
import { useEnrichmentNotifications } from './hooks/useEnrichmentEvent'
import { ThemeProvider } from './hooks/useTheme'
import { ToastProvider, useToast } from './hooks/useToast'
import { ToastContainer } from './components/notifications/ToastContainer'
import { LookupsProvider } from './hooks/useLookups'
import { CompanyProvider } from './hooks/CompanyProvider'
import { GlobalErrorHandler } from './components/GlobalErrorHandler'
import { AIChatWidget } from './components/aiChat'

// Create protected page components with routing parameters
const DashboardPage = ({ params }: { params?: { clientCompanyId: string, workspaceId: string } }) => (
  <ProtectedRoute>
    <WorkspaceShell clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} activeSection={Section.Dashboard}>
      <Dashboard selectedWorkspaceId={params!.workspaceId} />
    </WorkspaceShell>
  </ProtectedRoute>
)

// Targeted-company profile (ORB-623). Lives under People, since its subject is the people this
// workspace has reached at that company rather than the company itself.
const CompanyProfilePageComponent = ({ params }: { params?: { clientCompanyId: string, workspaceId: string, companyId: string, tab?: string } }) => (
  <ProtectedRoute>
    <WorkspaceShell
      clientCompanyId={params!.clientCompanyId}
      workspaceId={params!.workspaceId}
      activeSection={Section.Leads}
      detailFallbackSection={Section.Leads}
    >
      <CompanyProfilePage
        clientCompanyId={params!.clientCompanyId}
        workspaceId={params!.workspaceId}
        companyId={params!.companyId}
        tab={params!.tab}
      />
    </WorkspaceShell>
  </ProtectedRoute>
)

const CampaignsPage = ({ params }: { params?: { clientCompanyId: string, workspaceId: string } }) => (
  <ProtectedRoute>
    <WorkspaceShell clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} activeSection={Section.Campaigns}>
      <Campaigns selectedWorkspaceId={params!.workspaceId} clientCompanyId={params!.clientCompanyId} />
    </WorkspaceShell>
  </ProtectedRoute>
)

const EmailsPage = ({ params }: { params?: { clientCompanyId: string, workspaceId: string } }) => (
  <ProtectedRoute>
    <WorkspaceShell clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} activeSection={Section.Emails}>
      <WorkspaceEmailActivityPage clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} />
    </WorkspaceShell>
  </ProtectedRoute>
)

const AudiencePage = ({ params }: { params?: { clientCompanyId: string, workspaceId: string } }) => (
  <ProtectedRoute>
    <WorkspaceShell clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} activeSection={Section.Audience}>
      <Audience selectedCompanyId={params!.clientCompanyId} selectedWorkspaceId={params!.workspaceId} />
    </WorkspaceShell>
  </ProtectedRoute>
)

const WorkspaceLeadsPage = ({ params }: { params?: { clientCompanyId: string, workspaceId: string } }) => (
  <ProtectedRoute>
    <WorkspaceShell clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} activeSection={Section.Leads}>
      <WorkspaceLeads clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} />
    </WorkspaceShell>
  </ProtectedRoute>
)

const CalendarPage = ({ params }: { params?: { clientCompanyId: string, workspaceId: string } }) => (
  <ProtectedRoute>
    <WorkspaceShell clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} activeSection={Section.Calendar}>
      <ScheduleXCalendar clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} />
    </WorkspaceShell>
  </ProtectedRoute>
)


const WorkspaceSettingsPageComponent = ({ params }: { params?: { clientCompanyId: string, workspaceId: string } }) => (
  <ProtectedRoute>
    <WorkspaceShell clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} activeSection={Section.Settings}>
      <WorkspaceSettingsPage clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} />
    </WorkspaceShell>
  </ProtectedRoute>
)

const KnowledgeBasePageComponent = ({ params }: { params?: { clientCompanyId: string, workspaceId: string } }) => (
  <ProtectedRoute>
    <WorkspaceShell clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} activeSection={Section.KnowledgeBase}>
      <KnowledgeBasePage clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} />
    </WorkspaceShell>
  </ProtectedRoute>
)

const CampaignDetailPageComponent = ({ params }: { params?: { clientCompanyId: string, workspaceId: string, campaignId: string } }) => (
  <ProtectedRoute>
    <WorkspaceShell clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} activeSection={Section.Campaigns} detailFallbackSection={Section.Campaigns}>
      <CampaignDetail clientCompanyId={params!.clientCompanyId} selectedWorkspaceId={params!.workspaceId} campaignId={params!.campaignId} />
    </WorkspaceShell>
  </ProtectedRoute>
)



const WebDetailPageComponent = ({ params }: { params?: { clientCompanyId: string, workspaceId: string, campaignId: string, webId: string } }) => (
  <ProtectedRoute>
    <WorkspaceShell clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} activeSection={Section.Campaigns} detailFallbackSection={Section.Campaigns}>
      <WebDetail clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} campaignId={params!.campaignId} webId={params!.webId} />
    </WorkspaceShell>
  </ProtectedRoute>
)


const AudienceDetailPageComponent = ({ params }: { params?: { clientCompanyId: string, workspaceId: string, campaignId: string, fileImportId: string } }) => (
  <ProtectedRoute>
    <WorkspaceShell clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} activeSection={Section.Campaigns} detailFallbackSection={Section.Campaigns}>
      <AudienceDetail clientCompanyId={params!.clientCompanyId} selectedWorkspaceId={params!.workspaceId} campaignId={params!.campaignId} fileImportId={params!.fileImportId} />
    </WorkspaceShell>
  </ProtectedRoute>
)

// Contact detail (ORB-383). One page for web + campaign scope; webId present => web scope. The anchor is
// an owned record (lead | audience). The trailing :tab? selects overview/activity/emails/profile.
const ContactDetailPageComponent = ({ params }: { params?: { clientCompanyId: string, workspaceId: string, campaignId?: string, webId?: string, anchorType: string, anchorId: string, tab?: string } }) => (
  <ProtectedRoute>
    <WorkspaceShell clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} activeSection={params!.campaignId ? Section.Campaigns : Section.Leads} detailFallbackSection={params!.campaignId ? Section.Campaigns : Section.Leads}>
      <ContactDetailPage
        clientCompanyId={params!.clientCompanyId}
        workspaceId={params!.workspaceId}
        campaignId={params!.campaignId}
        webId={params!.webId}
        anchorType={params!.anchorType as ContactAnchorType}
        anchorId={params!.anchorId}
      />
    </WorkspaceShell>
  </ProtectedRoute>
)

// Blast detail (ORB-384): the deep-linkable Broadcasts detail for one blast under a web.
const BlastDetailPageComponent = ({ params }: { params?: { clientCompanyId: string, workspaceId: string, campaignId: string, webId: string, blastId: string } }) => (
  <ProtectedRoute>
    <WorkspaceShell clientCompanyId={params!.clientCompanyId} workspaceId={params!.workspaceId} activeSection={Section.Campaigns} detailFallbackSection={Section.Campaigns}>
      <BlastDetailPage
        clientCompanyId={params!.clientCompanyId}
        workspaceId={params!.workspaceId}
        campaignId={params!.campaignId}
        webId={params!.webId}
        blastId={params!.blastId}
      />
    </WorkspaceShell>
  </ProtectedRoute>
)

// Company-only route: bootstrap and route to default dashboard
const CompanyBootstrapPage = ({ params }: { params?: { clientCompanyId: string } }) => (
  <ProtectedRoute>
    <CompanyBootstrap clientCompanyId={params!.clientCompanyId} />
  </ProtectedRoute>
)

// Settings pages (Admin)
const SettingsCompanyPage = ({ params }: { params?: { clientCompanyId: string } }) => (
  <ProtectedRoute>
    <Settings
      activeSection="settings/company"
      clientCompanyId={params?.clientCompanyId}
    />
  </ProtectedRoute>
)

const SettingsAuthenticationPage = ({ params }: { params?: { clientCompanyId: string } }) => (
  <ProtectedRoute>
    <Settings
      activeSection="settings/authentication"
      clientCompanyId={params?.clientCompanyId}
    />
  </ProtectedRoute>
)

const SettingsIntegrationsPage = ({ params }: { params?: { clientCompanyId: string } }) => (
  <ProtectedRoute>
    <Settings
      activeSection="settings/integrations"
      clientCompanyId={params?.clientCompanyId}
    />
  </ProtectedRoute>
)

const SettingsSendingDomainsPage = ({ params }: { params?: { clientCompanyId: string } }) => (
  <ProtectedRoute>
    <Settings
      activeSection="settings/sending-domains"
      clientCompanyId={params?.clientCompanyId}
    />
  </ProtectedRoute>
)

const SettingsReplyToAddressesPage = ({ params }: { params?: { clientCompanyId: string } }) => (
  <ProtectedRoute>
    <Settings
      activeSection="settings/reply-to-addresses"
      clientCompanyId={params?.clientCompanyId}
    />
  </ProtectedRoute>
)

const SettingsSuppressionsPage = ({ params }: { params?: { clientCompanyId: string } }) => (
  <ProtectedRoute>
    <Settings
      activeSection="settings/suppression-list"
      clientCompanyId={params?.clientCompanyId}
    />
  </ProtectedRoute>
)

const SettingsUsersPage = ({ params }: { params?: { clientCompanyId: string } }) => (
  <ProtectedRoute>
    <Settings
      activeSection="settings/users"
      clientCompanyId={params?.clientCompanyId}
    />
  </ProtectedRoute>
)

const SettingsCustomersPage = ({ params }: { params?: { clientCompanyId: string } }) => (
  <ProtectedRoute>
    <Settings
      activeSection="settings/customers"
      clientCompanyId={params?.clientCompanyId}
    />
  </ProtectedRoute>
)

const SettingsKnowledgeBasePage = ({ params }: { params?: { clientCompanyId: string } }) => (
  <ProtectedRoute>
    <Settings
      activeSection="settings/knowledge-base"
      clientCompanyId={params?.clientCompanyId}
    />
  </ProtectedRoute>
)

export function App() {
  return (
    <LocationProvider>
      <ErrorBoundary>
        <ThemeProvider>
            <ToastProvider>
              <AuthProvider>
                <LookupsProvider>
                  <CompanyProvider>
                    <AppContent />
                  </CompanyProvider>
                </LookupsProvider>
              </AuthProvider>
            </ToastProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </LocationProvider>
  )
}

/** Isolates toast rendering so toast changes only re-render this component, not the entire app tree. */
function ToastRenderer() {
  const { toasts, removeToast } = useToast()
  return <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
}

function AppContent() {
  const { isAuthenticated, userProfile, isParentCompany } = useAuth()
  const location = useLocation()

  // Global enrichment-complete toast (ORB-383): fires wherever the user is, even after they leave the
  // contact. Mounted authenticated-session-wide so a re-enrich they kicked off still notifies them.
  useEnrichmentNotifications()

  // Only show AI chat if authenticated, has user profile, on company route, but NOT on admin settings pages
  // Admin settings routes: /company/:id/settings/company, /settings/authentication, etc.
  // Web settings tab: /company/.../web/:webId/settings — this should still show the chat
  // isParentCompany gates to parent (top-level) company employees only
  const shouldShowAIChat = isAuthenticated &&
    userProfile &&
    isParentCompany &&
    location?.path?.includes('/company/') &&
    !/\/company\/[^/]+\/settings\//.test(location?.path || '') &&
    true // landing-page exclusion removed — LP now lives in Assets overlay

  return (
    <>
      {/* Global error handler for 422 Business Rule errors */}
      <GlobalErrorHandler />
      
      <Router>
        <Route path="/" component={RootRedirect} />
        <Route path="/login" component={SignIn} />
        <Route path="/404" component={NotFound} />
        {/* Company workspace routes */}
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/audience" component={AudiencePage} />
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/leads" component={WorkspaceLeadsPage} />
        {/* Same page, Companies half. A path segment rather than local state so the list can be
            linked to — and so a company profile has a breadcrumb parent (ORB-623). */}
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/leads/companies" component={WorkspaceLeadsPage} />
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/calendar" component={CalendarPage} />
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/dashboard" component={DashboardPage} />
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/campaigns" component={CampaignsPage} />
        {/* Targeted-company profile (ORB-623). Plural segment: /company/:id is the client company. */}
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/companies/:companyId/:tab?" component={CompanyProfilePageComponent} />
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/emails" component={EmailsPage} />
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/settings" component={WorkspaceSettingsPageComponent} />
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/knowledge-base" component={KnowledgeBasePageComponent} />
        {/** Webinars section removed */}
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/campaign/:campaignId" component={CampaignDetailPageComponent} />

        {/* Web detail deep-link: leads (contacts/companies) now rendered inside WebDetail */}
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/campaign/:campaignId/web/:webId/leads/:sub?" component={WebDetailPageComponent} />
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/campaign/:campaignId/web/:webId/activations" component={WebDetailPageComponent} />
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/campaign/:campaignId/web/:webId/email-sends" component={WebDetailPageComponent} />
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/campaign/:campaignId/web/:webId/meetings" component={WebDetailPageComponent} />
        {/* Meeting detail (ORB-436): routed full-page recording + transcript overlay over the Meetings tab */}
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/campaign/:campaignId/web/:webId/meeting/:touchPointId" component={WebDetailPageComponent} />
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/campaign/:campaignId/web/:webId/calendar" component={WebDetailPageComponent} />
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/campaign/:campaignId/web/:webId/media" component={WebDetailPageComponent} />
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/campaign/:campaignId/web/:webId/landing-page" component={WebDetailPageComponent} />
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/campaign/:campaignId/web/:webId/settings" component={WebDetailPageComponent} />
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/campaign/:campaignId/web/:webId" component={WebDetailPageComponent} />

        {/* Blast detail (ORB-384): deep-linkable Broadcasts detail for one blast */}
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/campaign/:campaignId/web/:webId/broadcast/:blastId" component={BlastDetailPageComponent} />

        <Route path="/company/:clientCompanyId/workspace/:workspaceId/campaign/:campaignId/audience/:fileImportId" component={AudienceDetailPageComponent} />

        {/* Contact detail (ORB-383): web + campaign + cross-campaign (workspace) scope; trailing :tab? selects the tab */}
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/campaign/:campaignId/web/:webId/contact/:anchorType/:anchorId/:tab?" component={ContactDetailPageComponent} />
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/campaign/:campaignId/contact/:anchorType/:anchorId/:tab?" component={ContactDetailPageComponent} />
        <Route path="/company/:clientCompanyId/workspace/:workspaceId/contact/:anchorType/:anchorId/:tab?" component={ContactDetailPageComponent} />
        <Route path="/company/:clientCompanyId" component={CompanyBootstrapPage} />
        <Route path="/company/:clientCompanyId/settings/company" component={SettingsCompanyPage} />
        <Route path="/company/:clientCompanyId/settings/authentication" component={SettingsAuthenticationPage} />
        <Route path="/company/:clientCompanyId/settings/integrations" component={SettingsIntegrationsPage} />
        <Route path="/company/:clientCompanyId/settings/sending-domains" component={SettingsSendingDomainsPage} />
        <Route path="/company/:clientCompanyId/settings/reply-to-addresses" component={SettingsReplyToAddressesPage} />
        <Route path="/company/:clientCompanyId/settings/suppression-list" component={SettingsSuppressionsPage} />
        <Route path="/company/:clientCompanyId/settings/customers" component={SettingsCustomersPage} />
        <Route path="/company/:clientCompanyId/settings/users" component={SettingsUsersPage} />
        <Route path="/company/:clientCompanyId/settings/knowledge-base" component={SettingsKnowledgeBasePage} />
        {/* Catch-all route for 404 */}
        <Route default component={NotFound} />
      </Router>

      {/* Global Toast Container */}
      <ToastRenderer />
      
      {/* AI Chat Widget - only show when authenticated and in company context */}
      {shouldShowAIChat && <AIChatWidget />}
    </>
  )
}
